import { useState, useEffect, useCallback, useRef } from 'react';
import { logoAPI } from '../services/api';
import api from '../services/api';

// Default cleanup patterns for domain guessing
const DEFAULT_CLEANUP_REGEX = /\s*(inc|llc|corp|corporation|company|co|ltd|limited)\s*/gi;

/**
 * Guess domain from a name using a provided domain map
 * Falls back to converting the name to a .com domain
 * @param {string} name - Company/service name
 * @param {Object} domainMap - Known name→domain mappings
 * @param {RegExp} cleanupRegex - Regex for removing noise words before domain guessing
 */
function guessDomain(name, domainMap = {}, cleanupRegex = DEFAULT_CLEANUP_REGEX) {
  const normalized = name.toLowerCase().trim();
  
  // Check exact and partial matches in domain map
  for (const [key, domain] of Object.entries(domainMap)) {
    if (normalized.includes(key)) {
      return domain;
    }
  }
  
  // Fallback: try to convert name to domain format
  const cleaned = normalized
    .replace(cleanupRegex, '')
    .trim();
  
  if (cleaned.length >= 2) {
    return cleaned.replace(/[^a-z0-9]/g, '') + '.com';
  }
  
  return null;
}

/**
 * Custom hook for auto-fetching company/service logos
 * 
 * Auto-searches once when the name first reaches 2+ characters.
 * Once a logo is found or manually selected, auto-search is locked
 * and won't fire again unless:
 *   - The name field is completely cleared (resets lock)
 *   - The user explicitly calls retry() 
 *   - The user calls clear() to remove the logo
 * 
 * @param {string} name - The company/service name to search for
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in ms (default: 1000)
 * @param {Object} options.domainMap - Map of known name→domain mappings for fallback
 * @returns {Object} Logo state and actions
 */
export default function useLogoFetch(name, { debounceMs = 1000, domainMap = {}, cleanupRegex = DEFAULT_CLEANUP_REGEX } = {}) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoOptions, setLogoOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [logoLocked, setLogoLocked] = useState(false);
  const timeoutRef = useRef(null);
  const lastFetchedRef = useRef('');
  const lastNameRef = useRef('');
  const hasAutoSearched = useRef(false);

  const fetchLogos = useCallback(async (searchName) => {
    if (!searchName || searchName.trim().length < 2) {
      setLogoUrl(null);
      setLogoOptions([]);
      setError(null);
      return;
    }

    // Don't re-fetch if we already fetched this name
    if (lastFetchedRef.current === searchName) {
      return;
    }

    setLoading(true);
    setError(null);
    setLogoOptions([]);
    lastFetchedRef.current = searchName;

    try {
      // First try the search API for multiple options
      const response = await logoAPI.search(searchName, 10);
      
      if (response.data.success && response.data.options && response.data.options.length > 0) {
        const options = response.data.options;
        setLogoOptions(options);
        // Auto-select first option
        setLogoUrl(options[0].dataUrl);
        setLogoLocked(true);
        hasAutoSearched.current = true;
        setError(null);
      } else {
        // Fallback to domain guessing if search returns nothing
        const domain = guessDomain(searchName, domainMap, cleanupRegex);
        if (domain) {
          try {
            const fallbackResponse = await api.get('/proxy/logo?domain=' + encodeURIComponent(domain));
            if (fallbackResponse.data.success && fallbackResponse.data.dataUrl) {
              setLogoUrl(fallbackResponse.data.dataUrl);
              setLogoLocked(true);
              hasAutoSearched.current = true;
              setLogoOptions([{ 
                domain, 
                name: searchName, 
                dataUrl: fallbackResponse.data.dataUrl 
              }]);
            } else {
              setLogoUrl(null);
              setError('Could not find logo');
            }
          } catch {
            setLogoUrl(null);
            setError('Could not find logo');
          }
        } else {
          setLogoUrl(null);
          setError('Could not find logo');
        }
      }
    } catch (err) {
      console.log('Could not fetch logo:', err.message);
      setLogoUrl(null);
      setLogoOptions([]);
      setError('Could not find logo');
    } finally {
      setLoading(false);
    }
  }, [domainMap, cleanupRegex]);

  // Debounced auto-search effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If name is cleared, unlock so next entry triggers auto-search
    if (!name || name.trim().length < 2) {
      lastNameRef.current = name;
      if (!name || name.trim().length === 0) {
        setLogoLocked(false);
        hasAutoSearched.current = false;
        lastFetchedRef.current = '';
      }
      return;
    }

    // Don't auto-search if logo is locked (user already has a logo)
    if (logoLocked || hasAutoSearched.current) {
      lastNameRef.current = name;
      return;
    }

    // Check if we already fetched for this exact name
    if (lastNameRef.current === name) {
      return;
    }

    lastNameRef.current = name;

    // Debounce the fetch
    timeoutRef.current = setTimeout(() => {
      fetchLogos(name);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [name, debounceMs, fetchLogos, logoLocked]);

  const retry = useCallback(() => {
    setLogoLocked(false);
    hasAutoSearched.current = false;
    lastFetchedRef.current = '';
    fetchLogos(name);
  }, [name, fetchLogos]);

  const clear = useCallback(() => {
    setLogoUrl(null);
    setLogoOptions([]);
    setError(null);
    setLogoLocked(false);
    hasAutoSearched.current = false;
    lastFetchedRef.current = '';
  }, []);

  const selectOption = useCallback((option) => {
    setLogoUrl(option.dataUrl);
    setLogoLocked(true);
    setShowPicker(false);
  }, []);

  // Wrapper that locks when setting a logo externally (e.g. from existing data)
  const setLogoUrlAndLock = useCallback((url) => {
    setLogoUrl(url);
    if (url) {
      setLogoLocked(true);
      hasAutoSearched.current = true;
    }
  }, []);

  return { 
    logoUrl, 
    logoOptions,
    loading, 
    error, 
    retry, 
    clear, 
    setLogoUrl: setLogoUrlAndLock,
    showPicker,
    setShowPicker,
    selectOption
  };
}
